/**
 * AuraCategoriesModal - Shared multi-select aura category picker.
 *
 * Single source of truth for the "Radiate Aura" UX. Used everywhere a user
 * gives aura: from the post feed (`AuraPill`), the message threads
 * (`MessageAuraPill`), and the composers that pre-tag aura before sending
 * (`AddPostForm`, `PostComments`). The modal owns its own selection state and
 * loads categories + daily stats on open; the parent controls open/close and
 * decides what happens on radiate (immediate `createAura` calls, or queueing
 * pending aura for a not-yet-created post/comment).
 *
 * onRadiate is called with `{ selection, totalSelected, entries }` where
 * `entries` is an array of `{ categoryId, points }` aggregated per category in
 * display order.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { selectDailyStats, setDailyStats } from '../../store/aura.reducer';
import { getAuraCategories, getUserDailyPointStats } from '../../helpers/auraHelpers';
import ModalShell from '../../modals/ModalShell';

const GRID_ROW_INDICES = [
  [0, 1, 2],
  [3, 4, 5, 6],
  [7, 8, 9],
];

function AuraCategoriesModal({
  open,
  onClose,
  onRadiate,
  loading = false,
  title = 'Radiate Aura',
  radiateLabel = 'Radiate Aura',
}) {
  const dispatch = useDispatch();
  const [categories, setCategories] = useState([]);
  const [selection, setSelection] = useState({});
  const dailyStatsFromStore = useSelector(selectDailyStats);
  const [localStats, setLocalStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const dailyStats = localStats ?? dailyStatsFromStore;

  const slots = useMemo(() => {
    const list = categories.slice(0, 10);
    const out = Array(10).fill(null);
    list.forEach((c, i) => {
      if (i < 10) out[i] = c;
    });
    return out;
  }, [categories]);

  const totalSelected = useMemo(
    () => Object.values(selection).reduce((a, n) => a + (Number(n) || 0), 0),
    [selection]
  );

  const remainingToday = dailyStats?.remainingToday;
  const canAddMore = !statsLoading && remainingToday != null && totalSelected < remainingToday;

  const orderedSelectedChips = useMemo(() => {
    const chips = [];
    for (const cat of categories) {
      const id = cat.aura_category_id;
      const c = selection[String(id)] ?? 0;
      if (c > 0) chips.push({ ...cat, count: c });
    }
    return chips;
  }, [categories, selection]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setStatsLoading(true);
    setLocalStats(null);
    getAuraCategories()
      .then((cats) => {
        if (!cancelled) setCategories(Array.isArray(cats) ? cats : []);
      })
      .catch(() => toast.error('Failed to load categories'));
    getUserDailyPointStats()
      .then((s) => {
        if (cancelled) return;
        setLocalStats(s);
        dispatch(setDailyStats(s));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) setSelection({});
  }, [open]);

  const addCategory = useCallback(
    (categoryId) => {
      if (loading) return;
      if (!canAddMore) {
        toast.error('No aura points left today.');
        return;
      }
      const key = String(categoryId);
      setSelection((prev) => ({
        ...prev,
        [key]: (prev[key] ?? 0) + 1,
      }));
    },
    [loading, canAddMore]
  );

  const clearCategory = useCallback((categoryId) => {
    const key = String(categoryId);
    setSelection((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleRadiate = () => {
    if (loading) return;
    if (totalSelected === 0) {
      toast.error('Select at least one category.');
      return;
    }
    if (remainingToday != null && totalSelected > remainingToday) {
      toast.error('Not enough aura points left today for this selection.');
      return;
    }
    const entries = orderedSelectedChips.map((c) => ({
      categoryId: c.aura_category_id,
      points: c.count,
    }));
    onRadiate?.({ selection, totalSelected, entries });
  };

  if (!open) return null;

  return (
    <ModalShell title={title} onClose={onClose} panelClassName="max-w-2xl">
      <p className="text-silver text-sm mb-3">
        {statsLoading
          ? 'Checking aura points left today...'
          : dailyStats
          ? `${dailyStats.remainingToday} aura points left today`
          : 'Could not load aura points left today.'}
      </p>

      {orderedSelectedChips.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2 min-h-[2.5rem]">
          {orderedSelectedChips.map((cat) => (
            <div
              key={cat.aura_category_id}
              className="relative inline-flex items-center gap-1.5 pl-2 pr-7 py-1.5 rounded-full bg-shadow border border-aura/30 text-sm text-ash"
            >
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: cat.icon_color || '#8B7CFF' }}
              />
              <span>{cat.category_name}</span>
              <span className="text-silver tabular-nums">×{cat.count}</span>
              <button
                type="button"
                onClick={() => clearCategory(cat.aura_category_id)}
                className="absolute top-0.5 right-0.5 flex h-6 w-6 items-center justify-center rounded-full text-silver hover:bg-shadow hover:text-ash text-lg leading-none"
                aria-label={`Remove all ${cat.category_name} aura`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {GRID_ROW_INDICES.map((row, rowIdx) => {
          const colClass =
            rowIdx === 1 ? 'grid grid-cols-4 gap-2' : 'grid grid-cols-3 gap-2';
          return (
            <div key={rowIdx} className={colClass}>
              {row.map((slotIdx) => {
                const cat = slots[slotIdx];
                if (!cat) {
                  return (
                    <div
                      key={`empty-${slotIdx}`}
                      className="min-h-[3rem] rounded-xl border border-transparent"
                      aria-hidden="true"
                    />
                  );
                }
                const sid = String(cat.aura_category_id);
                const n = selection[sid] ?? 0;
                return (
                  <button
                    key={cat.aura_category_id}
                    type="button"
                    disabled={loading || !canAddMore}
                    onClick={() => addCategory(cat.aura_category_id)}
                    aria-label={`Add ${cat.category_name} aura`}
                    className="relative flex min-h-[3rem] flex-col items-center justify-center gap-1 rounded-xl border border-shadow bg-void/40 px-2 py-2 text-center text-sm text-ash hover:bg-shadow hover:border-aura/40 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: cat.icon_color || '#8B7CFF' }}
                    />
                    <span className="leading-tight break-words">{cat.category_name}</span>
                    {n > 0 && (
                      <span className="absolute top-1 right-1 rounded-full bg-aura/30 px-1.5 text-xs font-semibold tabular-nums text-aura">
                        {n}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          disabled={loading || totalSelected === 0}
          onClick={handleRadiate}
          className="rounded-xl bg-aura px-5 py-2.5 text-void font-medium hover:opacity-90 disabled:opacity-40 disabled:pointer-events-none"
        >
          {loading ? 'Sending…' : radiateLabel}
        </button>
      </div>
    </ModalShell>
  );
}

export default AuraCategoriesModal;
